import { ENotificationType } from '@shared/notifications/lib';
import { useNotifications } from '@shared/notifications/model';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { BaseCheckBox, BaseLink, BaseTextButton, BaseTextField } from '@evrone-erp/uikit';
import { Box } from '@mui/material';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import qs from 'query-string';
import { getErrorMessage, ROUTES } from '@shared/lib';
import { PlateFormContainer } from '@shared/ui';
import { useLogin } from '@features/auth/api';
import type { TLoginFormFields, TLoginFormProps } from '@features/auth/lib/types';
import { SCHEME } from '@features/auth/lib/loginScheme';
const ATTEMPT_COUNT_LIMIT = 3;
const DEFAULT_VALUES: TLoginFormFields = {
  username: '',
  password: '',
  rememberMe: false
};
export const LoginForm: React.FC<TLoginFormProps> = ({
  onSuccess
}) => {
  const {
    t
  } = useTranslation();
  const [attemptCount, setAttemptCount] = useState(0);
  const {
    query
  } = useRouter();
  const {
    control,
    handleSubmit,
    formState: {
      isValid
    }
  } = useForm<TLoginFormFields>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(SCHEME)
  });
  const token = typeof query?.token === 'string' ? query.token : '';
  const queryString = qs.stringify(query, {
    skipEmptyString: true
  });
  const handleErrorOnLogin = () => {
    setAttemptCount(count => count + 1);
  };
  const {
    mutate: login,
    error,
    isError,
    isLoading
  } = useLogin({
    options: {
      onError: handleErrorOnLogin,
      onSuccess: ({
        data
      }) => onSuccess(data)
    }
  });
  const forgotPasswordHref = queryString ? `${ROUTES.forgotPassword}?${queryString}` : ROUTES.forgotPassword;
  const isAttemptCountReachedLimit = attemptCount >= ATTEMPT_COUNT_LIMIT;
  const summaryErrorText = isAttemptCountReachedLimit ? <span>
      {t('maybeForgotPassword')}{' '}
      <BaseLink href={forgotPasswordHref} noSign>
        {t('here')}
      </BaseLink>
    </span> : isError && error && getErrorMessage(error);
  const {
    showNotification
  } = useNotifications();
  const handleSuccessSubmit: SubmitHandler<TLoginFormFields> = data => {
    if (!token) {
      showNotification({
        message: t('noToken'),
        type: ENotificationType.error
      });
      return;
    }
    login({
      email: data.username,
      password: data.password,
      rememberMe: data.rememberMe,
      token
    });
  };
  return <form autoComplete="on" onSubmit={handleSubmit(handleSuccessSubmit)}>
      <PlateFormContainer formTitle={t('welcomeBack')} headerTitle={t('enter')} errorContent={summaryErrorText} headerLink={<BaseLink href={forgotPasswordHref} noSign>
            {t('forgotPassword')}
          </BaseLink>} footerPrimaryAction={<BaseTextButton type="submit" disabled={!isValid} isLoading={isLoading} iconType="plus">
            {t('signIn')}
          </BaseTextButton>} footerSecondaryAction={<Controller name="rememberMe" control={control} render={({
      field
    }) => <BaseCheckBox label={t('rememberMe')} value={field.value} onChange={field.onChange} />} />}>
        <Box>
          <Controller name="username" control={control} render={({
          field
        }) => <BaseTextField type="email" name={field.name} label={t('login')} value={field.value} fullWidth onChange={field.onChange} onBlur={field.onBlur} autoComplete="username" />} />
        </Box>

        <Box>
          <Controller control={control} name="password" render={({
          field
        }) => <BaseTextField type="password" name={field.name} label={t('password')} value={field.value} fullWidth onChange={field.onChange} onBlur={field.onBlur} autoComplete="current-password" />} />
        </Box>
      </PlateFormContainer>
    </form>;
};